import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe, NgStyle } from '@angular/common';
import { EEntityType, EPriceType, ERelationType } from '@web/dtos';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { OnDemandPricingStore } from '../../../../services/on-demand/on-demand-pricing.service';
import { OnDemandSelectionStateService } from '../../../../services/on-demand/on-demand-selection-state.service';
import { OnDemandJourneyViewModel } from '../on-demand-journey-view-model';
import { getTransportName } from '@web/shared-functions/journey';

@Component({
  selector: 'aw-on-demand-ticket-configuration-summary',
  templateUrl: './on-demand-ticket-configuration-summary.component.html',
  imports: [NgStyle, DatePipe, FilterTextsPipe, LanguagePipe],
})
export class OnDemandTicketConfigurationSummaryComponent {
  readonly showReservationDeadline = input(false);

  protected readonly ERelationType = ERelationType;
  protected readonly EEntityType = EEntityType;

  protected readonly corporateDesign = inject(CorporateDesignService);
  protected readonly selectionState = inject(OnDemandSelectionStateService);
  protected readonly textStore = inject(WebTextStore);
  protected readonly pricingStore = inject(OnDemandPricingStore);
  private readonly journeyDisplay = inject(OnDemandJourneyViewModel);

  protected readonly trip = this.journeyDisplay.trip;
  protected readonly tripDate = this.journeyDisplay.tripDate;
  protected readonly astOrigin = computed(
    () => this.journeyDisplay.onDemandLeg()?.origin?.name ?? ''
  );
  protected readonly astDestination = computed(
    () => this.journeyDisplay.onDemandLeg()?.destination?.name ?? ''
  );
  protected readonly onDemandTransportName = computed(() => {
    const leg = this.journeyDisplay.onDemandLeg();

    return leg
      ? getTransportName(leg, this.journeyDisplay.operatorName())
      : this.journeyDisplay.operatorName();
  });
  protected readonly regularLeg = this.journeyDisplay.regularLeg;
  protected readonly regularDestination =
    this.journeyDisplay.regularDestination;
  protected readonly regularTransportNames = computed(() => [
    ...new Set(
      this.journeyDisplay
        .regularLegs()
        .map(leg => getTransportName(leg))
        .filter(Boolean)
    ),
  ]);
  protected readonly showPublicTransport = computed(() => !!this.regularLeg());
  protected readonly reservationDeadline = computed(() =>
    this.selectionState.getReservationDeadlineLabel()
  );
  protected readonly bookableServiceOptions = computed(
    () =>
      this.selectionState.selectedConnectionOption()?.bookableServiceOptions ??
      []
  );
  protected readonly expandedStates = signal<boolean[]>([]);

  constructor() {
    effect(() => {
      const count = this.selectionState.passengerTicketSelections().length;
      if (this.expandedStates().length !== count) {
        this.expandedStates.set(
          Array.from({ length: count }, (_, index) => index === 0)
        );
      }
    });
  }

  protected togglePassengerExpanded(index: number): void {
    this.expandedStates.update(states => {
      const updated = [...states];
      updated[index] = !updated[index];
      return updated;
    });
  }

  protected getOnDemandSinglePriceLabel(fahrgasttyp: string): string {
    return this.pricingStore
      .getPrice(EPriceType.ON_DEMAND, fahrgasttyp)
      .toLocaleString('de-AT', {
        style: 'currency',
        currency: 'EUR',
      });
  }

  protected getComfortSinglePriceLabel(fahrgasttyp: string): string {
    return this.pricingStore
      .getPrice(EPriceType.COMFORT, fahrgasttyp)
      .toLocaleString('de-AT', {
        style: 'currency',
        currency: 'EUR',
      });
  }

  protected getExtraEntries(
    passengerIndex: number
  ): { label: string; count: number }[] {
    const extras =
      this.selectionState.extrasPerPassenger()[passengerIndex] ?? [];
    const counts = new Map<string, number>();

    for (const extra of extras.filter(Boolean)) {
      counts.set(extra, (counts.get(extra) ?? 0) + 1);
    }

    return Array.from(counts.entries()).map(([key, count]) => ({
      label:
        this.bookableServiceOptions().find(
          service => service.mobilityUserField === key
        )?.label ?? this.selectionState.getExtraLabel(key),
      count,
    }));
  }
}
